"use client";

import * as React from "react";

// @components
import {
  Legend,
  Tooltip,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  } from "recharts";
import {
  Button,
  Spinner,
  ChartBlock,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  ChartScaleToggle,
  exploreChartLines,
  chartXAxis,
  chartYAxis,
} from "@/components";

// @icons
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

// @hooks
import { useChartScale } from "@/hooks/use-chart-scale";
import { useInView } from "@/hooks/use-in-view";
import { useStakingParticipation } from "@/hooks/use-shares";

// @utils
import {
  exploreChartMargin,

  gridConfig,
  legendConfig,
  exploreLegendProps,
  tooltipConfig,
  toggleHideChart,
  prepareChartDataForScale
} from "@/utils";

export function StakingParticipation() {
  const ref = React.useRef(null);
  const { logScale, setLogScale } = useChartScale(true);
  const isInView = useInView(ref);
  const [variation, setVariation] = React.useState("");
  const [hide, setHide] = React.useState<string[]>([]);
  const { data, isLoading } = useStakingParticipation(isInView, variation);

  const fields = data?.chart?.fields;
  const chartVariations = data?.chart?.variations;
  const chartActiveVariation = variation || data?.chart?.currentVariation;

  const lines = React.useMemo(() => {
    if (fields) {
      return Object.keys(fields)?.map((el) => {
        if (fields[el]?.hidden) {
          setHide((prev) => [...prev, el]);
        }

        return {
          dataKey: el,
          name: fields[el].title,
          stroke: fields[el].color,
        };
      });
    }

    return [];
  }, [fields]);

  const chartScaleData = React.useMemo(
    () => prepareChartDataForScale(data?.data, lines, logScale),
    [data?.data, lines, logScale],
  );

  return (
    <ChartBlock
      ref={ref}
      border="bottom"
      isLoading={isLoading}
      title="Staking Participation & Maximum APY"
      slot={
        <div className="flex flex-wrap items-center gap-2">
          <ChartScaleToggle logScale={logScale} onChange={setLogScale} />          <DropdownMenu modal={false}>
          <DropdownMenuTrigger
            asChild
            aria-label="Variation Selector"
            className="group outline-none focus-visible:outline-none"
          >
            <Button variant="simple">
              {isLoading ? (
                <Spinner width={18} height={18} />
              ) : (
                <>
                  {chartVariations?.[variation || chartActiveVariation] ||
                    "Variation"}
                  <ChevronDownIcon className="h-3.5 w-3.5 stroke-2 transition-transform group-data-[state=open]:rotate-180" />
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {chartVariations
              ? Object.keys(chartVariations)?.map((el) => (
                  <DropdownMenuItem key={el} onClick={() => setVariation(el)}>
                    <span className="mr-4">{chartVariations?.[el]}</span>
                    {el === chartActiveVariation && (
                      <CheckIcon className="ml-auto h-4 w-4 stroke-2" />
                    )}
                  </DropdownMenuItem>
                ))
              : null}
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartScaleData.data} margin={exploreChartMargin}>
          {chartXAxis()}
          {chartYAxis({ label: "Staking Participation [%] - APY [%]", logScale, valueFormat: "percent", domain: chartScaleData.yDomain })}
          <Legend
            {...exploreLegendProps({ lineKeys: lines.map((line) => line.dataKey), hidden: hide, onHiddenChange: setHide })}
          />
          <Tooltip {...tooltipConfig()} />
          <CartesianGrid {...gridConfig()} />
          {lines.length > 0 ? exploreChartLines(lines, hide) : null}
        </LineChart>
      </ResponsiveContainer>
    </ChartBlock>
  );
}
